import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { errMsg, fmtMoney, fmtQty, fmtDate } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, FileDown, Mail, BellRing, Link2, CalendarCheck, Search, ArrowUpDown, X, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { ExportButtons } from "../components/ExportButtons";
import { EvidencePanel } from "../components/EvidencePanel";
import { SendPoEmailDialog } from "../components/SendPoEmailDialog";
import { CostCodeHeader, AccountPicker, CostCodeChip } from "../components/CostCode";
import { ItemCombobox } from "../components/ItemCombobox";
import { SearchSelect } from "../components/SearchSelect";

const TAX_RATES = [16, 8, 0];
const PO_STATUSES = ["pending_l1", "pending_l2", "rejected", "open", "partial", "received", "cancelled"];
const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
const emptyLine = () => ({ item_number: "", part_number: "", description: "", qty: 1, unit_cost: 0, equipment_code: "", cost_code: { subcuenta: "", tipo: "" } });
const emptyForm = () => ({ id: null, key: newKey(), supplier_id: "", department: "", notes: "", currency: "MXN", tax_rate: 16, quotation_number: "",
  delivery_date: "", payment_terms: "", ship_to_warehouse: "", shipping_cost: 0, other_costs: 0,
  client_code: "", contract_code: "", frente_code: "",
  lines: [emptyLine()] });

const calc = (f) => {
  const subtotal = f.lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_cost) || 0), 0);
  const tax = subtotal * (Number(f.tax_rate) || 0) / 100;
  return { subtotal, tax, total: subtotal + tax + (Number(f.shipping_cost) || 0) + (Number(f.other_costs) || 0) };
};
const fmtDMY = (iso) => { if (!iso) return "—"; const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : iso; };

export default function PurchaseOrders() {
  const { t } = useI18n();
  const { can, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [l2Slots, setL2Slots] = useState([]);
  const [currencies, setCurrencies] = useState([{ code: "MXN", rate_to_base: 1 }]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectText, setRejectText] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const quick = searchParams.get("overdue") ? "overdue" : searchParams.get("responses") ? "responses" : "";

  // Filters / sort
  const [fQ, setFQ] = useState(""); const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fSupplier, setFSupplier] = useState("");
  const [fFrom, setFFrom] = useState(""); const [fTo, setFTo] = useState("");
  const [sort, setSort] = useState({ by: "created_at", dir: "desc" });
  useEffect(() => { const h = setTimeout(() => setQ(fQ.trim()), 300); return () => clearTimeout(h); }, [fQ]);
  const toggleSort = (by) => setSort((s) => ({ by, dir: s.by === by && s.dir === "desc" ? "asc" : "desc" }));
  const SortTh = ({ col, children, className = "" }) => (
    <th className={`cursor-pointer select-none ${className}`} onClick={() => toggleSort(col)} data-testid={`po-sort-${col}`}>
      <span className="inline-flex items-center gap-1">{children}<ArrowUpDown size={9} className={sort.by === col ? "text-primary" : "opacity-40"} /></span>
    </th>
  );

  const load = useCallback(() => {
    const params = { sort_by: sort.by, sort_dir: sort.dir };
    if (q) params.q = q;
    if (fStatus !== "all") params.status = fStatus === "pending" ? "pending_l1,pending_l2" : fStatus;
    if (fSupplier) params.supplier_id = fSupplier;
    if (fFrom) params.date_from = fFrom;
    if (fTo) params.date_to = fTo;
    return api.get("/purchase-orders", { params }).then((r) => {
      setRows(r.data);
      const openId = searchParams.get("open");
      if (openId) { const po = r.data.find((p) => p.id === openId); if (po) setDetail(po); }
    });
  }, [searchParams, q, fStatus, fSupplier, fFrom, fTo, sort]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/equipment").then((r) => setEquipment(r.data)).catch(() => {});
    api.get("/address-book", { params: { type: "supplier" } }).then((r) => setSuppliers(r.data));
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/currencies").then((r) => setCurrencies(r.data)).catch(() => {});
    api.get("/settings/po-approvers").then((r) => setL2Slots(r.data.slots || [])).catch(() => {});
  }, []);

  const setLine = (i, patch) => { const lines = [...form.lines]; lines[i] = { ...lines[i], ...patch }; setForm({ ...form, lines }); };
  const onSupplier = (v) => {
    const s = suppliers.find((x) => x.id === v);
    const cur = s?.default_currency && currencies.some((c) => c.code === s.default_currency) ? s.default_currency : form.currency;
    setForm({ ...form, supplier_id: v, currency: cur, tax_rate: s?.tax_rate ?? form.tax_rate, payment_terms: s?.payment_terms || form.payment_terms });
  };

  const openNew = () => { setForm(emptyForm()); setOpen(true); };
  const openEdit = (po) => {
    setForm({ id: po.id, key: newKey(), supplier_id: po.supplier_id, department: po.department || "", notes: po.notes || "", currency: po.currency || "MXN",
      tax_rate: po.tax_rate ?? 16, quotation_number: po.quotation_number || "", delivery_date: po.delivery_date || "",
      payment_terms: po.payment_terms || "", ship_to_warehouse: po.ship_to_warehouse || "",
      shipping_cost: po.shipping_cost || 0, other_costs: po.other_costs || 0,
      client_code: po.client_code || "", contract_code: po.contract_code || "", frente_code: po.frente_code || "",
      lines: po.lines.map((l) => ({ item_number: l.item_number, part_number: l.part_number || "", description: l.description || "", qty: l.qty, unit_cost: l.unit_cost, equipment_code: l.equipment_code || "",
        cost_code: { subcuenta: l.cost_code?.subcuenta || "", tipo: l.cost_code?.tipo || "" } })) });
    setDetail(null); setOpen(true);
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = { supplier_id: form.supplier_id, department: form.department, notes: form.notes, currency: form.currency,
        tax_rate: Number(form.tax_rate), quotation_number: form.quotation_number, delivery_date: form.delivery_date, payment_terms: form.payment_terms,
        ship_to_warehouse: form.ship_to_warehouse, shipping_cost: Number(form.shipping_cost) || 0, other_costs: Number(form.other_costs) || 0,
        client_code: form.client_code, contract_code: form.contract_code, frente_code: form.frente_code, idempotency_key: form.key,
        lines: form.lines.filter((l) => l.item_number).map((l) => ({ item_number: l.item_number, qty: Number(l.qty), unit_cost: Number(l.unit_cost),
          equipment_code: l.equipment_code || "", cost_code: { subcuenta: l.cost_code?.subcuenta || "", tipo: l.cost_code?.tipo || "" } })) };
      if (form.id) await api.put(`/purchase-orders/${form.id}`, payload);
      else await api.post("/purchase-orders", payload);
      toast.success(form.id ? t("common.saved") : `${t("common.created")} · enviada a autorización`);
      setOpen(false); setForm(emptyForm()); load();
    } catch (e) { toast.error(errMsg(e)); if (!form.id) setForm((f) => ({ ...f, key: newKey() })); }
    finally { setSaving(false); }
  };

  const cancel = async (id) => {
    try { await api.post(`/purchase-orders/${id}/cancel`); toast.success(t("common.saved")); setDetail(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openPdf = (po) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/purchase-orders/${po.id}/pdf`, "_blank");
  const refreshDetail = async (id) => { const { data } = await api.get("/purchase-orders"); setRows(data); setDetail(data.find((p) => p.id === id) || null); };
  const approve = async (id) => {
    try { const { data } = await api.post(`/purchase-orders/${id}/approve`, {}); toast.success(data.status === "open" ? "OC autorizada (nivel 2) · abierta" : "Autorizada por encargado · pasa a nivel 2"); setDetail(data); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const reject = async () => {
    if (rejectText.trim().length < 3) return toast.error("Motivo obligatorio");
    try { const { data } = await api.post(`/purchase-orders/${detail.id}/reject`, { reason: rejectText.trim() }); toast.success("OC rechazada"); setDetail(data); setRejectOpen(false); setRejectText(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const acceptDate = async (id) => {
    try { await api.post(`/purchase-orders/${id}/accept-proposed-date`); toast.success(t("common.saved")); refreshDetail(id); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const runReminders = async () => {
    try { const { data } = await api.post("/purchase-orders/delivery-reminders/run"); toast.success(`${t("po.remindersDone")}: ${data.count}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const ackResponse = async (id) => {
    try { await api.post(`/purchase-orders/${id}/ack-supplier-response`); toast.success(t("common.saved")); refreshDetail(id); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = (po) => ["open", "partial"].includes(po.status) && po.delivery_date && po.delivery_date < today;
  const visibleRows = quick === "overdue" ? rows.filter(isOverdue)
    : quick === "responses" ? rows.filter((p) => ["proposed", "rejected"].includes(p.supplier_confirmation?.status) && !p.supplier_confirmation?.acknowledged_at)
    : rows;
  const CONF_CLS = { sent: "border-amber-300 bg-amber-50 text-amber-800", confirmed: "border-emerald-300 bg-emerald-50 text-emerald-700", proposed: "border-sky-300 bg-sky-50 text-sky-800", rejected: "border-red-300 bg-red-50 text-red-700" };
  const totals = calc(form);
  const isPending = (po) => ["pending_l1", "pending_l2"].includes(po.status);
  const canEdit = (po) => ["pending_l1", "pending_l2", "rejected", "open"].includes(po.status) && !po.lines.some((l) => l.received_qty > 0) && can("admin", "buyer");
  const deptOf = (name) => departments.find((d) => d.name === name);
  const mayApprove = (po) => {
    if (!isPending(po) || po.created_by === user?.email) return user?.role === "admin" && isPending(po);
    if (user?.role === "admin") return true;
    if (po.status === "pending_l1") { const ids = (deptOf(po.department)?.approvers || []).map((a) => a.user_id); return ids.length ? ids.includes(user?.id) : user?.role === "approver"; }
    return l2Slots.some((s) => s.user_id === user?.id);
  };
  const lbl = "text-[10px] font-bold uppercase tracking-widest";
  const supplierOf = (po) => suppliers.find((s) => s.id === po.supplier_id);

  return (
    <div data-testid="purchase-orders-page">
      <PageHeader title={t("po.title")} subtitle={t("po.subtitle")}>
        {can("admin", "buyer", "approver", "warehouse") && <ExportButtons path="/reports/purchase-orders" filename="ordenes_compra" testid="export-pos" />}
        {can("admin", "buyer") && (
          <Button size="sm" variant="outline" className="rounded-none text-xs font-bold" data-testid="po-run-reminders" onClick={runReminders}>
            <BellRing size={13} className="mr-1" /> {t("po.runReminders")}
          </Button>
        )}
        {can("admin", "buyer") && (
          <Button size="sm" className="rounded-none text-xs font-bold" data-testid="po-new-button" onClick={openNew}>
            <Plus size={13} className="mr-1" /> {t("po.new")}
          </Button>
        )}
      </PageHeader>

      {/* Filters */}
      <div className="border bg-card p-3 mb-4 flex flex-wrap gap-2 items-end" data-testid="po-filters">
        <div className="space-y-0.5 flex-1 min-w-56">
          <Label className={lbl}>{t("common.search")}</Label>
          <div className="relative">
            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={fQ} onChange={(e) => setFQ(e.target.value)} placeholder="Folio OC, requisición, proveedor, artículo, No. de parte…" className="rounded-none h-8 text-xs pl-6" data-testid="po-search-input" />
          </div>
        </div>
        <div className="space-y-0.5">
          <Label className={lbl}>{t("common.status")}</Label>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="rounded-none h-8 text-xs w-44" data-testid="po-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")}</SelectItem>
              <SelectItem value="pending">Pendientes de autorizar</SelectItem>
              {PO_STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`st.${s}`) === `st.${s}` ? s : t(`st.${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-0.5 w-56">
          <Label className={lbl}>{t("common.supplier")}</Label>
          <SearchSelect value={fSupplier} onChange={(v) => setFSupplier(v || "")} testid="po-filter-supplier" placeholder="Proveedor…" emptyLabel={t("common.all")}
            options={suppliers.map((s) => ({ value: s.id, label: s.name, hint: s.tax_id || "" }))} />
        </div>
        <div className="space-y-0.5"><Label className={lbl}>Desde</Label><Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="rounded-none h-8 text-xs w-36" data-testid="po-filter-from" /></div>
        <div className="space-y-0.5"><Label className={lbl}>Hasta</Label><Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className="rounded-none h-8 text-xs w-36" data-testid="po-filter-to" /></div>
        {(fQ || fStatus !== "all" || fSupplier || fFrom || fTo) && (
          <Button size="sm" variant="ghost" className="rounded-none text-xs h-8" onClick={() => { setFQ(""); setFStatus("all"); setFSupplier(""); setFFrom(""); setFTo(""); }} data-testid="po-filter-clear"><X size={12} className="mr-1" /> Limpiar</Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="uppercase font-black">{form.id ? t("po.edit") : t("po.new")}</DialogTitle>
            <DialogDescription className="text-xs">{t("po.subtitle")} · Al guardar pasa a autorización: encargado de departamento → nivel 2.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1 col-span-2">
              <Label className={lbl}>{t("common.supplier")}</Label>
              <SearchSelect value={form.supplier_id} onChange={(v) => v && onSupplier(v)} testid="po-form-supplier" placeholder="Buscar proveedor…"
                options={suppliers.map((s) => ({ value: s.id, label: s.name, hint: s.tax_id ? `RFC ${s.tax_id}` : "" }))} />
            </div>
            <div className="space-y-1">
              <Label className={lbl}>{t("cur.code")}</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger className="rounded-none h-8 text-xs" data-testid="po-form-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className={lbl}>Departamento (autoriza nivel 1)</Label>
              <Select value={form.department || "_none"} onValueChange={(v) => setForm({ ...form, department: v === "_none" ? "" : v })}>
                <SelectTrigger className="rounded-none h-8 text-xs" data-testid="po-form-department"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="_none">—</SelectItem>{departments.filter((d) => d.active !== false).map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className={lbl}>{t("po.quotation")}</Label>
              <Input value={form.quotation_number} onChange={(e) => setForm({ ...form, quotation_number: e.target.value })} className="rounded-none h-8 text-xs" data-testid="po-form-quotation" />
            </div>
            <div className="space-y-1">
              <Label className={lbl}>{t("po.deliveryDate")}</Label>
              <Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} className="rounded-none h-8 text-xs" data-testid="po-form-delivery-date" />
            </div>
            <div className="space-y-1">
              <Label className={lbl}>{t("po.taxRate")}</Label>
              <Select value={String(form.tax_rate)} onValueChange={(v) => setForm({ ...form, tax_rate: Number(v) })}>
                <SelectTrigger className="rounded-none h-8 text-xs" data-testid="po-form-tax-rate"><SelectValue /></SelectTrigger>
                <SelectContent>{TAX_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className={lbl}>{t("po.paymentTerms")}</Label>
              <Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="N30" className="rounded-none h-8 text-xs" data-testid="po-form-payment-terms" />
            </div>
            <div className="space-y-1">
              <Label className={lbl}>{t("po.shipTo")}</Label>
              <Select value={form.ship_to_warehouse || "_none"} onValueChange={(v) => setForm({ ...form, ship_to_warehouse: v === "_none" ? "" : v })}>
                <SelectTrigger className="rounded-none h-8 text-xs" data-testid="po-form-ship-to"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="_none">—</SelectItem>{warehouses.map((w) => <SelectItem key={w.id} value={w.code}>{w.code} — {w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1 col-span-3">
              <Label className={lbl}>{t("common.notes")} <span className="normal-case text-muted-foreground">· {t("po.notesHint")}</span></Label>
              <Textarea value={form.notes} rows={2} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none text-xs" data-testid="po-form-notes" />
            </div>
            <div className="col-span-3"><CostCodeHeader value={form} onChange={(patch) => setForm({ ...form, ...patch })} testid="po-cc" /></div>
          </div>
          <div className="space-y-2">
            <Label className={lbl}>{t("common.lines")}</Label>
            {form.lines.map((l, i) => (
              <div key={i} className="border p-2 space-y-1.5" data-testid={`po-line-${i}`}>
                <div className="flex gap-2 items-center">
                  <div className="flex-1 min-w-0">
                    <ItemCombobox value={l.item_number} testid={`po-line-item-${i}`}
                      onChange={(item_number, it) => setLine(i, { item_number, part_number: it?.part_number || "", description: it?.description || "", unit_cost: it?.standard_cost ?? l.unit_cost })} />
                  </div>
                  <Input type="number" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} className="rounded-none h-8 text-xs w-20" data-testid={`po-line-qty-${i}`} />
                  <Input type="number" value={l.unit_cost} onChange={(e) => setLine(i, { unit_cost: e.target.value })} className="rounded-none h-8 text-xs w-28" data-testid={`po-line-cost-${i}`} />
                  <button onClick={() => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-destructive" data-testid={`po-line-remove-${i}`}><Trash2 size={13} /></button>
                </div>
                {l.item_number && <div className="text-[11px] text-muted-foreground pl-1 truncate">{l.description}{l.part_number && <span className="ml-2 font-mono text-amber-700">NP {l.part_number}</span>}</div>}
                <div className="flex gap-2 items-center">
                  <SearchSelect value={l.equipment_code || ""} className="w-56" testid={`po-line-eq-${i}`} placeholder="Equipo (opcional)" emptyLabel="Sin equipo"
                    options={equipment.map((eq) => ({ value: eq.code, label: `${eq.code} — ${eq.name}`, hint: eq.model || "" }))}
                    onChange={(v) => setLine(i, { equipment_code: v || "" })} />
                  <div className="flex-1"><AccountPicker value={l.cost_code} onChange={(patch) => setLine(i, { cost_code: { ...(l.cost_code || {}), ...patch } })}
                    header={form} equipmentCode={l.equipment_code} testid={`po-line-cc-${i}`} /></div>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" className="rounded-none text-xs" data-testid="po-add-line-button" onClick={() => setForm({ ...form, lines: [...form.lines, emptyLine()] })}>
              <Plus size={12} className="mr-1" /> {t("common.addLine")}
            </Button>
          </div>
          <div className="flex justify-between items-end gap-4 border-t pt-3">
            <div className="flex gap-3">
              <div className="space-y-1"><Label className={lbl}>{t("po.shipping")}</Label>
                <Input type="number" min="0" value={form.shipping_cost} onChange={(e) => setForm({ ...form, shipping_cost: e.target.value })} className="rounded-none h-8 text-xs w-28" data-testid="po-form-shipping" /></div>
              <div className="space-y-1"><Label className={lbl}>{t("po.others")}</Label>
                <Input type="number" min="0" value={form.other_costs} onChange={(e) => setForm({ ...form, other_costs: e.target.value })} className="rounded-none h-8 text-xs w-28" data-testid="po-form-others" /></div>
            </div>
            <div className="text-xs text-right tabular-nums space-y-0.5" data-testid="po-form-totals">
              <div>{t("po.subtotal")}: <b>{fmtMoney(totals.subtotal)}</b></div>
              <div>{t("po.tax")} {form.tax_rate}%: <b>{fmtMoney(totals.tax)}</b></div>
              <div className="text-sm">{t("common.total")} {form.currency}: <b>{fmtMoney(totals.total)}</b></div>
            </div>
          </div>
          <Button onClick={save} disabled={saving || !form.supplier_id || !form.lines.some((l) => l.item_number)} className="rounded-none font-bold" data-testid="po-save-button">
            {saving ? "Guardando…" : form.id ? t("po.save") : t("common.create")}
          </Button>
        </DialogContent>
      </Dialog>

      {quick && (
        <div className="flex items-center gap-2 mb-3 text-xs" data-testid="po-quick-filter">
          <span className="font-bold uppercase tracking-widest text-[10px] text-muted-foreground">{quick === "overdue" ? t("po.filterOverdue") : t("po.filterResponses")} · {visibleRows.length}</span>
          <Button size="sm" variant="ghost" className="rounded-none text-xs h-7" onClick={() => setSearchParams({})} data-testid="po-quick-filter-clear">{t("common.all")}</Button>
        </div>
      )}
      {visibleRows.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="po-table">
          <table className="tbl">
            <thead><tr>
              <SortTh col="number">{t("common.number")}</SortTh><SortTh col="requisition_number">{t("po.fromReq")}</SortTh>
              <SortTh col="status">{t("common.status")}</SortTh><SortTh col="supplier_name">{t("common.supplier")}</SortTh>
              <th>Departamento</th><SortTh col="delivery_date">{t("po.deliveryDate")}</SortTh><th className="num">{t("common.lines")}</th>
              <SortTh col="total" className="num">{t("common.total")}</SortTh><SortTh col="created_at">{t("common.date")}</SortTh><th></th>
            </tr></thead>
            <tbody>
              {visibleRows.map((r) => (
                <tr key={r.id} className="cursor-pointer" onClick={() => setDetail(r)} data-testid={`po-row-${r.number}`}>
                  <td className="font-bold">{r.number}</td>
                  <td className="text-muted-foreground" data-testid={`po-req-${r.number}`}>{r.requisition_number || "—"}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td>{r.supplier_name}</td>
                  <td className="text-[11px]">{r.department || "—"}</td>
                  <td className="text-muted-foreground tabular-nums">
                    {fmtDMY(r.delivery_date)}
                    {isOverdue(r) && <span className="ml-1 text-[9px] font-bold uppercase text-red-700 border border-red-300 bg-red-50 px-1" data-testid={`po-overdue-${r.number}`}>{t("po.overdue")}</span>}
                    {r.supplier_confirmation?.status && r.supplier_confirmation.status !== "sent" && (
                      <span className={`ml-1 text-[9px] font-bold uppercase border px-1 ${CONF_CLS[r.supplier_confirmation.status]}`}>{t(`po.conf.${r.supplier_confirmation.status}`)}</span>
                    )}
                  </td>
                  <td className="num">{r.lines.length}</td>
                  <td className="num font-bold">{fmtMoney(r.total)} <span className="text-[9px] text-muted-foreground">{r.currency || "MXN"}</span></td>
                  <td className="text-muted-foreground">{fmtDate(r.created_at)}</td>
                  <td><span className="text-[10px] font-bold text-primary" data-testid={`po-view-${r.number}`}>{t("common.view")}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-none sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="uppercase font-black flex items-center gap-3">{detail.number} <StatusBadge status={detail.status} /></DialogTitle>
                <DialogDescription className="text-xs">{t("po.subtitle")}</DialogDescription>
              </DialogHeader>

              {/* Approval workflow */}
              <div className="border p-3 text-xs space-y-2" data-testid="po-approval-panel">
                <div className="flex items-center gap-2 font-bold uppercase tracking-widest text-[10px] text-muted-foreground"><ShieldCheck size={12} /> Autorización en 2 niveles</div>
                <div className="grid grid-cols-2 gap-3">
                  {[["l1", "1 · Encargado de departamento", detail.department ? `Depto. ${detail.department}` : "Sin departamento → cualquier aprobador"],
                    ["l2", "2 · Autorización final", l2Slots.map((s) => s.label).filter(Boolean).join(" / ") || "Configurar en Ajustes"]].map(([lv, title, who]) => {
                    const sig = detail.approval?.[lv];
                    return (
                      <div key={lv} className={`border p-2 ${sig ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30" : "border-dashed"}`} data-testid={`po-approval-${lv}`}>
                        <div className="font-bold">{title}</div>
                        <div className="text-[10px] text-muted-foreground">{who}</div>
                        {sig ? <div className="mt-1 text-emerald-700 dark:text-emerald-400 font-bold"><CheckCircle2 size={11} className="inline mr-1" />{sig.name || sig.by} · {fmtDate(sig.at)}</div>
                          : <div className="mt-1 text-amber-700">Pendiente</div>}
                      </div>
                    );
                  })}
                </div>
                {detail.status === "rejected" && (
                  <div className="border border-destructive/40 bg-destructive/5 p-2" data-testid="po-rejection">
                    <b className="text-destructive">Rechazada:</b> {detail.rejection_reason} <span className="text-muted-foreground">— {detail.rejected_by} · {fmtDate(detail.rejected_at)}</span>
                    <div className="text-[10px] text-muted-foreground">Edita la OC para reenviarla a autorización.</div>
                  </div>
                )}
                {mayApprove(detail) && (
                  <div className="flex gap-2">
                    <Button size="sm" className="rounded-none text-xs font-bold" onClick={() => approve(detail.id)} data-testid="po-approve-button">
                      <CheckCircle2 size={12} className="mr-1" /> {detail.status === "pending_l1" ? "Autorizar (encargado depto)" : "Autorizar (nivel 2)"}
                    </Button>
                    <Button size="sm" variant="destructive" className="rounded-none text-xs font-bold" onClick={() => { setRejectText(""); setRejectOpen(true); }} data-testid="po-reject-button">
                      <XCircle size={12} className="mr-1" /> Rechazar
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex gap-2 flex-wrap items-center">
                <Button size="sm" variant="outline" className="rounded-none text-xs font-bold" onClick={() => openPdf(detail)} data-testid="po-pdf-button">
                  <FileDown size={12} className="mr-1" /> {t("po.pdf")}
                </Button>
                {can("admin", "buyer", "approver", "warehouse") && <ExportButtons path={`/reports/po/${detail.number}`} filename={detail.number} testid="export-po-detail" />}
                {["open", "partial", "received"].includes(detail.status) && can("admin", "buyer") && (
                  <Button size="sm" variant="outline" className="rounded-none text-xs font-bold" onClick={() => setEmailOpen(true)} data-testid="po-email-button">
                    <Mail size={12} className="mr-1" /> {t("po.sendEmail")}
                  </Button>
                )}
                {detail.sent_to_supplier_at && (
                  <span className="text-[10px] font-bold text-emerald-700 border border-emerald-300 bg-emerald-50 px-1.5 py-0.5" data-testid="po-sent-badge">
                    {t("po.sentOn")} {fmtDate(detail.sent_to_supplier_at)} · {(detail.sent_to_supplier_to || []).join(", ")}
                  </span>
                )}
                {canEdit(detail) && (
                  <Button size="sm" variant="outline" className="rounded-none text-xs font-bold" onClick={() => openEdit(detail)} data-testid="po-edit-button">
                    <Edit3 size={12} className="mr-1" /> {t("po.edit")}
                  </Button>
                )}
              </div>
              {detail.supplier_confirmation?.status && (
                <div className={`border p-3 text-xs flex flex-wrap items-center gap-3 ${CONF_CLS[detail.supplier_confirmation.status]}`} data-testid="po-supplier-confirmation">
                  <div className="flex-1 min-w-48">
                    <div className="text-[10px] font-bold uppercase tracking-widest">{t("po.supplierConf")}</div>
                    <div className="font-bold" data-testid="po-conf-status">{t(`po.conf.${detail.supplier_confirmation.status}`)}
                      {detail.supplier_confirmation.proposed_delivery_date && detail.supplier_confirmation.status === "proposed" && <> → <b>{detail.supplier_confirmation.proposed_delivery_date}</b></>}
                    </div>
                    {(detail.supplier_confirmation.contact_name || detail.supplier_confirmation.comments) && (
                      <div className="text-[11px] opacity-80">{detail.supplier_confirmation.contact_name}{detail.supplier_confirmation.comments && <> · "{detail.supplier_confirmation.comments}"</>}</div>
                    )}
                  </div>
                  {detail.supplier_confirmation.status === "proposed" && can("admin", "buyer") && (
                    <Button size="sm" className="rounded-none text-xs font-bold" onClick={() => acceptDate(detail.id)} data-testid="po-accept-date"><CalendarCheck size={12} className="mr-1" /> {t("po.acceptDate")}</Button>
                  )}
                  {["proposed", "rejected"].includes(detail.supplier_confirmation.status) && !detail.supplier_confirmation.acknowledged_at && can("admin", "buyer") && (
                    <Button size="sm" variant="outline" className="rounded-none text-xs font-bold" onClick={() => ackResponse(detail.id)} data-testid="po-ack-response">{t("po.ackResponse")}</Button>
                  )}
                  {detail.supplier_reminders_sent > 0 && <span className="text-[10px] opacity-80" data-testid="po-reminders-sent">{t("po.remindersSent")}: {detail.supplier_reminders_sent}</span>}
                  {detail.supplier_confirmation.token && (
                    <Button size="sm" variant="ghost" className="rounded-none text-xs h-7" data-testid="po-copy-link"
                      onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/po-confirm/${detail.supplier_confirmation.token}`); toast.success(t("set.copied")); }}>
                      <Link2 size={12} className="mr-1" /> {t("po.copyLink")}
                    </Button>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs" data-testid="po-detail-info">
                <div><span className="font-bold">{t("common.supplier")}:</span> {detail.supplier_name}</div>
                <div data-testid="po-detail-supplier-rfc"><span className="font-bold">RFC proveedor:</span> {detail.supplier_rfc || supplierOf(detail)?.tax_id || "—"}</div>
                <div data-testid="po-detail-currency"><span className="font-bold">{t("cur.code")}:</span> {detail.currency || "MXN"}
                  {(detail.currency || "MXN") !== "MXN" && <span className="text-muted-foreground"> · TC {detail.exchange_rate}</span>}</div>
                {detail.requisition_number && <div><span className="font-bold">{t("po.fromReq")}:</span> {detail.requisition_number}</div>}
                <div><span className="font-bold">Departamento:</span> {detail.department || "—"}</div>
                <div><span className="font-bold">{t("po.quotation")}:</span> {detail.quotation_number || "—"}</div>
                <div><span className="font-bold">{t("po.deliveryDate")}:</span> {fmtDMY(detail.delivery_date)}</div>
                <div><span className="font-bold">{t("po.paymentTerms")}:</span> {detail.payment_terms || "—"}</div>
                <div><span className="font-bold">{t("po.shipTo")}:</span> {detail.ship_to_warehouse || "—"}</div>
                {detail.requested_by && <div><span className="font-bold">{t("rq.requester")}:</span> {detail.requested_by_name || detail.requested_by}</div>}
                {detail.notes && <div className="col-span-2"><span className="font-bold">{t("common.notes")}:</span> {detail.notes}</div>}
              </div>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead><tr>
                    <th>{t("common.item")}</th><th>No. parte (fabricante)</th><th>{t("common.description")}</th><th className="num">{t("common.qty")}</th>
                    <th className="num">{t("po.receivedQty")}</th><th className="num">{t("common.cost")}</th><th className="num">{t("common.total")}</th>
                  </tr></thead>
                  <tbody>
                    {detail.lines.map((l, i) => (
                      <tr key={i}>
                        <td className="font-bold">{l.item_number}</td>
                        <td className="font-mono text-[11px]" data-testid={`po-detail-part-${i}`}>{l.part_number || "—"}</td>
                        <td>{l.description}<div><CostCodeChip cc={l.cost_code} testid={`po-detail-cc-${i}`} /></div></td>
                        <td className="num">{fmtQty(l.qty)}</td>
                        <td className={`num font-bold ${l.received_qty >= l.qty ? "text-green-700 dark:text-green-400" : ""}`}>{fmtQty(l.received_qty)}</td>
                        <td className="num">{fmtMoney(l.unit_cost)}</td>
                        <td className="num">{fmtMoney(l.qty * l.unit_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end">
                <div className="text-xs text-right tabular-nums space-y-0.5 border p-3 min-w-56" data-testid="po-detail-totals">
                  <div className="flex justify-between gap-6"><span>{t("po.subtotal")}</span><b>{fmtMoney(detail.subtotal ?? detail.total)}</b></div>
                  <div className="flex justify-between gap-6"><span>{t("po.tax")} {detail.tax_rate ?? 0}%</span><b>{fmtMoney(detail.tax_amount ?? 0)}</b></div>
                  {detail.shipping_cost > 0 && <div className="flex justify-between gap-6"><span>{t("po.shipping")}</span><b>{fmtMoney(detail.shipping_cost)}</b></div>}
                  {detail.other_costs > 0 && <div className="flex justify-between gap-6"><span>{t("po.others")}</span><b>{fmtMoney(detail.other_costs)}</b></div>}
                  <div className="flex justify-between gap-6 text-sm border-t pt-1"><span>{t("common.total")} {detail.currency || "MXN"}</span><b>{fmtMoney(detail.total)}</b></div>
                </div>
              </div>
              <EvidencePanel resourceType="po" basePath={`/purchase-orders/${detail.id}/evidence`} canWrite={can("admin", "buyer", "warehouse", "approver")} hideDelete={!can("admin", "buyer")} />
              {detail.requisition_id && (
                <div data-testid="po-req-attachments">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-2">{t("po.reqAttachments")} · {detail.requisition_number}</div>
                  <EvidencePanel resourceType="po-req" basePath={`/requisitions/${detail.requisition_id}/evidence`} canWrite={false} />
                </div>
              )}
              {detail.history?.length > 0 && (
                <details className="text-[11px] border p-2">
                  <summary className="font-bold uppercase tracking-widest cursor-pointer text-muted-foreground">Historial ({detail.history.length})</summary>
                  <ul className="mt-2 space-y-1">{detail.history.map((h, i) => (
                    <li key={i} className="flex gap-2 text-muted-foreground"><span className="font-bold uppercase">{h.action}</span><span>{h.by}</span><span>{fmtDate(h.at)}</span>{h.reason && <span className="text-destructive">— {h.reason}</span>}</li>
                  ))}</ul>
                </details>
              )}
              {["pending_l1", "pending_l2", "rejected", "open"].includes(detail.status) && can("admin", "buyer") && (
                <Button variant="destructive" size="sm" className="rounded-none text-xs font-bold w-fit" onClick={() => cancel(detail.id)} data-testid="po-cancel-button">{t("po.cancel")}</Button>
              )}
              {emailOpen && (
                <SendPoEmailDialog po={detail} open={emailOpen} onOpenChange={setEmailOpen}
                  supplierEmail={supplierOf(detail)?.email || ""} onSent={() => refreshDetail(detail.id)} />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader><DialogTitle className="uppercase font-black">Rechazar OC</DialogTitle><DialogDescription className="text-xs">Indica el motivo; el comprador podrá corregir y reenviar.</DialogDescription></DialogHeader>
          <Textarea value={rejectText} onChange={(e) => setRejectText(e.target.value)} rows={4} className="rounded-none text-xs" data-testid="po-reject-textarea" />
          <DialogFooter><Button variant="destructive" className="rounded-none font-bold text-xs" onClick={reject} data-testid="po-reject-confirm">Rechazar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
